import { Alert, Box, CircularProgress, Container, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useCreateOrganizationMutation,
  useFindOrganizationBySlugQuery,
} from "@/libs/generated/hooks";
import type { OrganizationFormSchemaType } from "@/schemas/organization-form";

import Form from "./Form";

function EditOrganization() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const client = useQueryClient();

  if (!slug) {
    throw new Error("Slug is required");
  }
  const { data, isPending: loading, error } = useFindOrganizationBySlugQuery({ slug });
  const createOrganization = useCreateOrganizationMutation({
    onSuccess: (data) => {
      toast.success(t("network.organization.created", { name: data.createOrganization.name }), {
        position: "bottom-right",
      });
      client.invalidateQueries({ queryKey: ["findAllOrganizations"] });
    },
    onError: (error) => {
      toast.error(error.message || t("network.errors.unknown"), {
        position: "bottom-right",
      });
    },
  });
  const dataValues = data?.findOrganizationBySlug;
  const onSubmit = async (formData: OrganizationFormSchemaType) => {
    await createOrganization.mutateAsync({
      CreateOrganizationInput: {
        ...formData,
      },
    });
    client.invalidateQueries({ queryKey: ["findOrganizationBySlug", { slug }] });
    client.invalidateQueries({ queryKey: ["findAllOrganizations"] });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading organization details : {error.message}
      </Alert>
    );
  }

  return (
    <Container maxWidth="lg">
      <Grid container sx={{ position: "relative", top: "80px", paddingBottom: 10 }}>
        <Grid size={{ xs: 12 }}>
          <Form onSubmit={onSubmit} values={dataValues} disabledFields={["assigneeEmail"]} />
        </Grid>
      </Grid>
    </Container>
  );
}

export default EditOrganization;
